import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { colors } from "../theme";
import { getShows, getSalesAssignments, getMarketingAssignments, getYieldByQuarter, generateDashboardFocus } from "../services/api";
import { mapShowRow, deriveStage } from "../config/showStage";
import { pct, aggregateMetrics, byEventCoverage, pickLaggard, yieldDeltas, deltaText, deltaDir } from "../config/dashboardMetrics";
import { KPI } from "../components/atoms";
import { Landmark, Building2, Megaphone, Upload } from "lucide-react";
import { FilterSelect, FilterClear } from "../components/FilterBar";
import AiSignals from "../components/AiSignals";

const F = "'Montserrat',sans-serif";
const M = F;

// Colour per "Where to focus" signal type (leadership strategic signals).
const FOCUS_COLOR = { coverage: colors.brand.primary, conversion: colors.status.good, trend: colors.brand.orange, capacity: colors.brand.magenta, portfolio: colors.brand.purple };

// Pipeline-health stage definitions (New → Awaiting → In Review → Completed)
const STAGE_DEFS = [
  { key: "new",      label: "New",             sub: "Enrichment pending", color: colors.brand.orange },
  { key: "awaiting", label: "Awaiting Review", sub: "Assign a reviewer",  color: colors.brand.magenta },
  { key: "review",   label: "In Review",       sub: "Review in progress", color: colors.brand.purple },
  { key: "done",     label: "Completed",       sub: "Review completed",   color: colors.status.good },
];

// ── Reusable card header: title + one-line caption (+ optional right slot) ──────
function CardHead({ title, caption, right }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: colors.text.heading }}>{title}</div>
        {right}
      </div>
      {caption && <div style={{ fontSize: 9.5, color: colors.text.muted, marginTop: 2, lineHeight: 1.4 }}>{caption}</div>}
    </div>
  );
}

// Management-only executive dashboard: competitor-show lead yield & coverage.
export default function DashboardPage() {
  const {
    nav, selectedEvent, changeEvent,
    currentRole, setCurrentRole,
    notifs, setNotifs, notifOpen, setNotifOpen,
    events = [],
  } = useApp();

  // Real ITE event list (from getEvents) → name lookup used across the page.
  const eventName = useCallback((id) => events.find(e => e.ite_event_id === id)?.name || id || "—", [events]);

  const [shows,  setShows]  = useState([]);
  const [salesA, setSalesA] = useState([]);
  const [mktA,   setMktA]   = useState([]);
  const [loading, setLoading] = useState(true);

  const [trend, setTrend] = useState([]);

  const [fEvent, setFEvent] = useState("all");
  const [fYear,  setFYear]  = useState("all");
  const yearDefaulted = useRef(false);
  const [pipelineOpen, setPipelineOpen] = useState(false); // operational detail — collapsed by default for execs

  // "Where to focus" — on-demand AI strategic signals (grounded in the numbers)
  const [focus, setFocus] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getShows().catch(() => []),
      getSalesAssignments({}).catch(() => []),
      getMarketingAssignments({}).catch(() => []),
    ]).then(([sh, sa, ma]) => {
      setShows((Array.isArray(sh) ? sh : []).map(mapShowRow));
      setSalesA(Array.isArray(sa) ? sa : []);
      setMktA(Array.isArray(ma) ? ma : []);
    }).finally(() => setLoading(false));
  }, []);

  // Lead yield over time — real conversion timing, bucketed by quarter (backend)
  useEffect(() => {
    const params = {};
    if (fEvent !== "all") params.ite_event_id = fEvent;
    if (fYear !== "all") params.year = fYear;
    getYieldByQuarter(params)
      .then(rows => setTrend((Array.isArray(rows) ? rows : []).map(r => ({
        p: `'${String(r.year).slice(2)} Q${r.quarter}`,
        campaign: r.campaign || 0,
        crm: r.crm || 0,
      }))))
      .catch(() => setTrend([]));
  }, [fEvent, fYear]);

  // Clear any generated focus signals when the filter scope changes (avoid stale AI).
  useEffect(() => { setFocus(null); setFocusError(null); }, [fEvent, fYear]);

  const salesByShow = useMemo(() => Object.fromEntries(salesA.map(a => [a.show_id, a])), [salesA]);
  const mktByShow   = useMemo(() => Object.fromEntries(mktA.map(a => [a.show_id, a])), [mktA]);

  const yearOpts = useMemo(
    () => [...new Set(shows.map(s => s.year).filter(Boolean))].sort((a, b) => b - a),
    [shows]
  );

  // Default the year filter to the current year once data loads (falling back to
  // the latest year available). Runs once so it never overrides a manual change.
  useEffect(() => {
    if (yearDefaulted.current || yearOpts.length === 0) return;
    yearDefaulted.current = true;
    const cy = String(new Date().getFullYear());
    setFYear(yearOpts.map(String).includes(cy) ? cy : String(yearOpts[0]));
  }, [yearOpts]);

  const fShows = useMemo(() => shows.filter(s =>
    (fEvent === "all" || s.event === fEvent) &&
    (fYear === "all" || String(s.year) === fYear)
  ), [shows, fEvent, fYear]);

  // ── Aggregate metrics (all from live data) ──────────────────────────────────
  const m = useMemo(() => aggregateMetrics(fShows, salesByShow, mktByShow), [fShows, salesByShow, mktByShow]);

  const eventsCovered = new Set(fShows.map(s => s.event)).size;

  // Per-event coverage: companies found + company-level review progress.
  const byEvent = useMemo(() => byEventCoverage(fShows, salesByShow, eventName), [fShows, salesByShow, eventName]);

  // Top competitor shows by yield (sales-qualified ÷ found)
  const topShows = useMemo(() => fShows.map(s => {
    const crm = (salesByShow[s.id]?.approved_count) || 0;
    return { name: s.name, year: s.year, found: s.companiesFound || 0, crm, yld: s.companiesFound ? Math.round(crm / s.companiesFound * 100) : 0 };
  }).sort((a, b) => b.crm - a.crm || b.found - a.found).slice(0, 6), [fShows, salesByShow]);

  // Laggard callout — the show that found the most companies yet converted none.
  const laggard = useMemo(() => pickLaggard(fShows, salesByShow), [fShows, salesByShow]);

  // Pipeline health — stage counts via deriveStage
  const stageCounts = useMemo(() => {
    const acc = {};
    fShows.forEach(s => {
      const st = deriveStage(s, salesByShow[s.id], mktByShow[s.id]);
      acc[st.key] = (acc[st.key] || 0) + 1;
    });
    return acc;
  }, [fShows, salesByShow, mktByShow]);

  const filterActive = fEvent !== "all" || fYear !== "all";

  // ── Shared styles ───────────────────────────────────────────────────────────
  const card = { background: colors.surface.card, border: `1px solid ${colors.border.subtle}`, borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" };
  // Four-layer lead-conversion funnel: found → enriched → marketing-qualified →
  // sales-qualified. Each layer keeps its own meaning colour (MQL magenta, SQL green).
  const funnelLayers = [
    { label: "Companies Found",     value: m.found,    color: colors.brand.primary },
    { label: "Enriched",            value: m.enriched, color: colors.brand.orange },
    { label: "Marketing Qualified", value: m.campaign, color: colors.brand.magenta },
    { label: "Sales Qualified",     value: m.crm,      color: colors.status.good },
  ];

  const openTracker = () => nav("portfolio");

  // ── Lead-yield derived values (headline + right-rail insights) ───────────────
  const { latestQ, prevQ, crmDelta, campDelta } = yieldDeltas(trend);

  // Generate leadership "Where to focus" signals from the current (filtered) numbers.
  const runFocus = () => {
    setFocusLoading(true);
    setFocusError(null);
    generateDashboardFocus({
      scope: fEvent === "all" ? "all events" : eventName(fEvent),
      year: fYear,
      found: m.found, enriched: m.enriched, sql: m.crm, mql: m.campaign,
      shows_tracked: fShows.length,
      events_covered: eventsCovered, events_total: events.length,
      quarter: latestQ?.p || null,
      crm_delta: crmDelta ?? null,
      top_shows: topShows.slice(0, 5).map(s => ({ name: s.name, yld: s.yld, found: s.found, crm: s.crm })),
      by_event: byEvent.slice(0, 8).map(e => ({ name: e.name, found: e.found, shows: e.shows, reviewed: e.reviewed })),
      trend: trend.map(t => ({ p: t.p, campaign: t.campaign, crm: t.crm })),
      pipeline: {
        new: stageCounts.new || 0,
        awaiting: stageCounts.awaiting || 0,
        review: stageCounts.review || 0,
        done: stageCounts.done || 0,
      },
    })
      .then(res => {
        if (res?.error) {
          setFocusError(String(res.error).includes("not configured")
            ? "AI isn't configured on this environment."
            : "Couldn't generate signals — please retry.");
          setFocus(null);
        } else {
          setFocus(Array.isArray(res?.signals) ? res.signals : []);
        }
      })
      .catch(() => setFocusError("Couldn't reach the AI service."))
      .finally(() => setFocusLoading(false));
  };

  return (
    <PageShell>
      <Sidebar active="dashboard" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 28px", background: colors.surface.card, borderBottom: `1px solid ${colors.border.subtle}` }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text.heading }}>Competitor Show Overview</div>
            <div style={{ fontSize: 11, color: colors.text.muted, marginTop: 2 }}>Lead yield &amp; coverage · <strong style={{ color: colors.text.heading }}>{fEvent === "all" ? "All Events" : eventName(fEvent)}</strong></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", background: colors.surface.subtle, borderRadius: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: colors.brand.primary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>MG</div>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Management</span>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "20px 28px 40px" }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: "center", color: colors.text.muted, fontSize: 13 }}>Loading dashboard…</div>
          ) : (
          <>
          {/* Filter + coverage summary bar (coverage only — conversion lives in the funnel) */}
          <div style={{ ...card, padding: "12px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: colors.text.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>View</span>
            <FilterSelect value={fEvent} onChange={e => setFEvent(e.target.value)} active={fEvent !== "all"}>
              <option value="all">All ITE events</option>
              {events.map(e => <option key={e.ite_event_id} value={e.ite_event_id}>{e.name}</option>)}
            </FilterSelect>
            <FilterSelect value={fYear} onChange={e => setFYear(e.target.value)} active={fYear !== "all"}>
              <option value="all">All time</option>
              {yearOpts.map(y => <option key={y} value={String(y)}>{y}</option>)}
            </FilterSelect>
            {filterActive && <FilterClear onClick={() => { setFEvent("all"); setFYear("all"); }} />}
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 11, color: colors.text.muted }}>
              <strong style={{ color: colors.text.heading, fontFamily: M }}>{eventsCovered}/{events.length}</strong> ITE events covered
            </div>
          </div>

          {/* KPI strip — the four headline numbers, each with a concise subtext */}
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <KPI color={colors.brand.primary} icon={<Landmark size={20} color={colors.brand.primary} />}
                 value={fShows.length} label="Competitor shows"
                 sub={fEvent === "all" ? "Tracked across all events" : "Tracked in this event"} />
            <KPI color={colors.brand.orange} icon={<Building2 size={20} color={colors.brand.orange} />}
                 value={m.found.toLocaleString()} label="Companies found"
                 sub={fEvent === "all" ? "Found across all competitor shows" : "Found in this event"} />
            <KPI color={colors.status.good} icon={<Upload size={20} color={colors.status.good} />}
                 value={`${pct(m.crm, m.found)}%`} label="Sales-qualified"
                 delta={deltaText(crmDelta, prevQ?.p)} deltaDir={deltaDir(crmDelta)}
                 sub={`${m.crm.toLocaleString()} of ${m.found.toLocaleString()} companies`} />
            <KPI color={colors.brand.magenta} icon={<Megaphone size={20} color={colors.brand.magenta} />}
                 value={`${pct(m.campaign, m.found)}%`} label="Marketing-qualified"
                 delta={deltaText(campDelta, prevQ?.p)} deltaDir={deltaDir(campDelta)}
                 sub={`${m.campaign.toLocaleString()} of ${m.found.toLocaleString()} companies`} />
          </div>

          {/* Lead yield over time — left: this-quarter headline + trend chart;
              right: key insights so the width is filled with meaning, not a gap. */}
          <div style={{ ...card, marginBottom: 12 }}>
            <CardHead
              title="Lead yield over time"
              caption="Leads reaching campaigns & sales by the quarter they were decided — is conversion improving?"
              right={
                <div style={{ display: "flex", gap: 14, fontSize: 9.5 }}>
                  <span style={{ color: colors.brand.magenta, fontWeight: 700 }}>● To campaign</span>
                  <span style={{ color: colors.status.good, fontWeight: 700 }}>● To Sales</span>
                </div>
              }
            />
            {trend.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: colors.text.muted, fontSize: 12 }}>No yield data yet — quarters appear here as leads are pushed to campaign or approved to sales.</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, alignItems: "start" }}>
                {/* Left: headline + chart */}
                <div>
                  {latestQ && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color: colors.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>This quarter · {latestQ.p}</div>
                      <div style={{ fontSize: 28, fontWeight: 800, fontFamily: M, color: colors.status.good, lineHeight: 1.15 }}>
                        {latestQ.crm}<span style={{ fontSize: 13, color: colors.text.muted, fontWeight: 600 }}> to sales</span>
                      </div>
                      <div style={{ fontSize: 13, color: colors.brand.magenta, fontWeight: 700 }}>{latestQ.campaign} to campaign</div>
                    </div>
                  )}
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={trend} margin={{ left: -18, right: 8, top: 4, bottom: 0 }} barGap={4} barCategoryGap="30%">
                      <XAxis dataKey="p" tick={{ fontSize: 10, fill: colors.text.muted }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: colors.text.muted }} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: colors.surface.subtle }} contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${colors.border.subtle}` }} />
                      <Bar dataKey="campaign" name="To campaign" fill={colors.brand.magenta} radius={[4, 4, 0, 0]} maxBarSize={44} />
                      <Bar dataKey="crm" name="To Sales" fill={colors.status.good} radius={[4, 4, 0, 0]} maxBarSize={44} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Right: Where to focus — on-demand AI strategic signals (grounded in the numbers) */}
                <div style={{ borderLeft: `1px solid ${colors.border.subtle}`, paddingLeft: 18 }}>
                  <AiSignals
                    title="Where to focus"
                    signals={focus}
                    loading={focusLoading}
                    error={focusError}
                    onGenerate={runFocus}
                    colorByType={FOCUS_COLOR}
                    emptyText="AI strategic recommendations from these numbers — click Generate."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Strategic row: top shows + coverage by event */}
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12, marginBottom: 12 }}>
            {/* Top shows by yield — a clean leaderboard (no progress bars) */}
            <div style={card}>
              <CardHead title="Top competitor shows by yield" caption="Which shows convert best — sales-qualified ÷ companies found." />
              {topShows.length === 0 ? (
                <div style={{ fontSize: 11, color: colors.text.muted }}>No shows for the selected filters.</div>
              ) : topShows.map((s, i) => (
                <div key={`${s.name}-${s.year}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i < topShows.length - 1 ? `1px solid ${colors.border.subtle}` : "none" }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: colors.brand.primaryTint, color: colors.brand.primary, fontWeight: 800, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.name} <span style={{ color: colors.text.muted, fontWeight: 500 }}>'{String(s.year).slice(2)}</span>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, fontFamily: M, color: s.yld > 0 ? colors.status.good : colors.text.faint }}>{s.yld}%</div>
                    <div style={{ fontSize: 9.5, color: colors.text.muted }}>{s.crm}/{s.found} qualified</div>
                  </div>
                </div>
              ))}
              {laggard && (
                <div style={{ fontSize: 11, color: colors.text.muted, marginTop: 8, paddingTop: 10, borderTop: `1px solid ${colors.border.subtle}` }}>
                  <strong style={{ color: colors.text.heading }}>{laggard.name} '{String(laggard.year).slice(2)}</strong> found {laggard.found.toLocaleString()} compan{laggard.found === 1 ? "y" : "ies"} but converted none — worth reviewing routing.
                </div>
              )}
            </div>

            {/* By ITE event — companies found (the number) + a review-status pill */}
            <div style={card}>
              <CardHead title="Coverage &amp; review by event" caption="Companies found per event, and how far its review is complete." />
              {byEvent.length === 0 ? (
                <div style={{ fontSize: 11, color: colors.text.muted }}>No shows for the selected filters.</div>
              ) : byEvent.map((e, i) => {
                const reviewPct = e.found ? Math.min(100, Math.round(e.reviewed / e.found * 100)) : 0;
                const complete = e.found > 0 && reviewPct >= 100;
                return (
                  <div key={e.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < byEvent.length - 1 ? `1px solid ${colors.border.subtle}` : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}</div>
                      <div style={{ fontSize: 10, color: colors.text.muted }}>{e.shows} show{e.shows > 1 ? "s" : ""}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 800, fontFamily: M, color: colors.text.heading }}>{e.found.toLocaleString()}</div>
                      <div style={{ fontSize: 9, color: colors.text.muted }}>companies</div>
                    </div>
                    <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, whiteSpace: "nowrap",
                      color: complete ? colors.status.good : colors.status.warnText, background: complete ? colors.status.goodBg : colors.status.warnBg }}>
                      {complete ? "✓ Reviewed" : `${reviewPct}% reviewed`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lead Conversion — left: 4-layer funnel; right: conversion ring */}
          <div style={{ ...card, marginBottom: 12 }}>
            <CardHead title="Lead Conversion" caption="How companies found narrow down to marketing- and sales-qualified leads." />
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24, alignItems: "center" }}>
              {/* Left: four left-aligned funnel layers */}
              <div>
                {funnelLayers.map(layer => {
                  const w = pct(layer.value, m.found);
                  return (
                    <div key={layer.label} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: colors.text.heading }}>{layer.label}</span>
                        <span style={{ fontSize: 12, fontFamily: M }}><b>{layer.value.toLocaleString()}</b> <span style={{ color: colors.text.muted, fontSize: 10.5 }}>· {w}%</span></span>
                      </div>
                      <div style={{ height: 12, background: colors.surface.subtle, borderRadius: 7, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.max(w, 2)}%`, background: layer.color, borderRadius: 7 }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right: conversion ring — the headline management metric */}
              <div style={{ borderLeft: `1px solid ${colors.border.subtle}`, paddingLeft: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: colors.text.muted, textTransform: "uppercase", letterSpacing: 0.4, alignSelf: "flex-start" }}>Conversion rate</div>
                <div style={{ width: 128, height: 128, borderRadius: "50%",
                  background: `conic-gradient(${colors.status.good} ${pct(m.crm, m.found) * 3.6}deg, ${colors.surface.subtle} 0)`,
                  display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <div style={{ width: 94, height: 94, borderRadius: "50%", background: colors.surface.card, display: "grid", placeItems: "center", textAlign: "center" }}>
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 800, fontFamily: M, color: colors.status.good, lineHeight: 1 }}>{pct(m.crm, m.found)}%</div>
                      <div style={{ fontSize: 9.5, color: colors.text.muted, marginTop: 2 }}>to sales-qualified</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: colors.text.body }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors.brand.magenta, flexShrink: 0 }} />
                  <span><b>{pct(m.campaign, m.found)}%</b> to marketing-qualified</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline health — operational detail, collapsed by default for execs */}
          <div style={card}>
            <div onClick={() => setPipelineOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: colors.text.heading }}>
                Pipeline health <span style={{ fontWeight: 400, color: colors.text.muted, fontSize: 10.5 }}>· review lifecycle (operational)</span>
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: colors.brand.primary }}>
                {pipelineOpen ? "▲ Hide" : "▼ Show operational detail"}
              </span>
            </div>
            {pipelineOpen && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <span onClick={openTracker} style={{ fontSize: 10, fontWeight: 700, color: colors.brand.primary, cursor: "pointer" }}>Open competitor tracker →</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {STAGE_DEFS.map((p, i) => {
                    const count = stageCounts[p.key] || 0;
                    return (
                      <React.Fragment key={p.key}>
                        <div onClick={openTracker} title={`View ${p.label} shows in the tracker`} style={{ flex: 1, textAlign: "center", cursor: "pointer", padding: "6px 4px", borderRadius: 8, transition: "background 0.12s" }}
                          onMouseEnter={e => e.currentTarget.style.background = colors.surface.subtle}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <div style={{ fontSize: 26, fontWeight: 800, fontFamily: M, letterSpacing: -1, color: count > 0 ? p.color : colors.text.faint }}>{count}</div>
                          <div style={{ fontSize: 10.5, fontWeight: 700, color: colors.text.heading, marginTop: 2 }}>{p.label}</div>
                          <div style={{ fontSize: 8.5, color: colors.text.muted, marginTop: 1 }}>{p.sub}</div>
                        </div>
                        {i < STAGE_DEFS.length - 1 && <div style={{ color: colors.border.strong, fontSize: 15, padding: "0 4px" }}>→</div>}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </PageShell>
  );
}
